import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ReminderButton } from "../components/ReminderComponents";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Search,
  Loader2,
  Package,
  FileText,
  Plus,
  Edit,
  Trash2,
  Container,
  MapPin,
  Calendar,
  Weight,
  Box,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Upload,
  Ship,
  CheckCircle,
  AlertCircle,
  Camera,
  Image,
  Settings,
  Mail,
  Tag,
  Save,
} from "lucide-react";


// LCL Co-loaders / NVOCCs
const LCL_COLOADERS = [
  { code: "FREIGHTLINKS", name: "Freightlinks" },
  { code: "SHIPCO", name: "Shipco Transport" },
  { code: "ECU", name: "ECU Worldwide" },
  { code: "BOXLINE", name: "Boxline" },
  { code: "SCAN_GLOBAL", name: "Scan Global Logistics" },
  { code: "OTHER", name: "Other / Unknown" },
];

export default function LCLTrackingPage() {
  const [activeTab, setActiveTab] = useState("track");
  const [hblNumber, setHblNumber] = useState("");
  const [coloader, setColoader] = useState("");  // Selected co-loader
  const [loading, setLoading] = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [manualHbls, setManualHbls] = useState([]);
  const [showEvents, setShowEvents] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingHbl, setEditingHbl] = useState(null);
  const [photos, setPhotos] = useState([]);  // Photos for current tracking
  const [showPhotos, setShowPhotos] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Advanced options for Europacific staff
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [europacificRef, setEuropacificRef] = useState("");
  const [notificationEmails, setNotificationEmails] = useState("");
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  const [formData, setFormData] = useState({
    hbl_number: "",
    container_number: "",
    shipper: "",
    consignee: "",
    origin: "",
    destination: "",
    pieces: "",
    weight: "",
    volume: "",
    status: "Pending",
    eta: "",
    notes: "",
  });
  
  // Bulk import state
  const [emailContent, setEmailContent] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const { getAuthHeaders, isEuropacificUser } = useAuth();

  const fetchPhotos = async (hblNumber) => {
    try {
      const response = await axios.get(`${API_URL}/photos/hbl/${hblNumber}`, {
        headers: getAuthHeaders(),
      });
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error("Failed to fetch photos:", error);
      setPhotos([]);
    }
  };

  const fetchManualHbls = async () => {
    try {
      const response = await axios.get(`${API_URL}/lcl/manual-hbls`, {
        headers: getAuthHeaders(),
      });
      setManualHbls(response.data);
    } catch (error) {
      console.error("Failed to fetch HBLs:", error);
    }
  };

  useEffect(() => {
    fetchManualHbls();
  }, []);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!hblNumber.trim()) {
      toast.error("Please enter an HBL number");
      return;
    }

    setLoading(true);
    setTrackingResult(null);

    try {
      const response = await axios.post(
        `${API_URL}/lcl/track-hbl`,
        { 
          hbl_number: hblNumber.trim(),
          coloader: coloader || null  // Selected co-loader
        },
        { headers: getAuthHeaders() }
      );
      setTrackingResult(response.data);
      toast.success("HBL found!");
      // Fetch photos for this HBL
      await fetchPhotos(hblNumber.trim());
      
      // Load existing notification settings if HBL has them
      if (response.data.europacific_ref) {
        setEuropacificRef(response.data.europacific_ref);
      } else {
        setEuropacificRef("");
      }
      if (response.data.notification_emails && response.data.notification_emails.length > 0) {
        setNotificationEmails(response.data.notification_emails.join(", "));
      } else {
        setNotificationEmails("");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info(error.response.data.detail || "HBL not found. Operations team notified.");
      } else {
        toast.error("Failed to track HBL");
      }
      setPhotos([]);
      setEuropacificRef("");
      setNotificationEmails("");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!trackingResult) return;
    
    setSavingNotifications(true);
    try {
      const emailList = notificationEmails.split(/[,;\s]+/).filter(e => e.trim());
      await axios.put(
        `${API_URL}/lcl/hbl/${trackingResult.hbl_number}/notifications`,
        {
          europacific_ref: europacificRef || null,
          notification_emails: emailList
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Notification settings saved!");
    } catch (error) {
      toast.error("Failed to save notification settings");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleAddHbl = async () => {
    if (!formData.hbl_number.trim()) {
      toast.error("HBL number is required");
      return;
    }

    setLoading(true);
    try {
      if (editingHbl) {
        await axios.put(
          `${API_URL}/lcl/manual-hbl/${editingHbl}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("HBL updated successfully");
      } else {
        await axios.post(`${API_URL}/lcl/manual-hbl`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("HBL created successfully");
      }
      setShowAddDialog(false);
      setEditingHbl(null);
      resetForm();
      fetchManualHbls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save HBL");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHbl = async (hblNumber) => {
    if (!confirm(`Are you sure you want to delete HBL ${hblNumber}?`)) return;

    try {
      await axios.delete(`${API_URL}/lcl/manual-hbl/${hblNumber}`, {
        headers: getAuthHeaders(),
      });
      toast.success("HBL deleted");
      fetchManualHbls();
    } catch (error) {
      toast.error("Failed to delete HBL");
    }
  };

  const handleEdit = (hbl) => {
    setEditingHbl(hbl.hbl_number);
    setFormData({
      hbl_number: hbl.hbl_number,
      container_number: hbl.container_number || "",
      shipper: hbl.shipper || "",
      consignee: hbl.consignee || "",
      origin: hbl.origin || "",
      destination: hbl.destination || "",
      pieces: hbl.pieces || "",
      weight: hbl.weight || "",
      volume: hbl.volume || "",
      status: hbl.status || "Pending",
      eta: hbl.eta || "",
      notes: hbl.notes || "",
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      hbl_number: "",
      container_number: "",
      shipper: "",
      consignee: "",
      origin: "",
      destination: "",
      pieces: "",
      weight: "",
      volume: "",
      status: "Pending",
      eta: "",
      notes: "",
    });
  };

  // Bulk Import Handlers
  const handleBulkImport = async () => {
    if (!emailContent.trim()) {
      toast.error("Please paste email content first");
      return;
    }

    setImportLoading(true);
    setImportResult(null);

    try {
      const response = await axios.post(
        `${API_URL}/lcl/bulk-import`,
        { email_content: emailContent },
        { headers: getAuthHeaders() }
      );
      setImportResult(response.data);
      if (response.data.success) {
        toast.success(`Imported ${response.data.hbls_created} new HBLs, updated ${response.data.hbls_updated}`);
        fetchManualHbls();
      } else {
        toast.error(response.data.errors?.[0] || "Import failed");
      }
    } catch (error) {
      toast.error("Failed to import HBLs");
    } finally {
      setImportLoading(false);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const text = e.dataTransfer.getData("text");
    if (text) {
      setEmailContent(text);
      return;
    }

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === "text/plain" || file.name.endsWith(".txt")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setEmailContent(e.target.result);
        };
        reader.readAsText(file);
      } else {
        toast.error("Please drop a text file or paste email content");
      }
    }
  }, []);

  const getStatusBadgeClass = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("delivered") || s.includes("completed")) return "status-success";
    if (s.includes("transit") || s.includes("shipped")) return "status-info";
    if (s.includes("pending") || s.includes("processing")) return "status-warning";
    return "bg-secondary text-muted-foreground";
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Package className="h-5 w-5 text-accent" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-foreground">
                LCL / BL Tracking
              </h1>
            </div>
            <p className="text-muted-foreground">
              Track LCL shipments by HBL, manage manual entries, or bulk import from email
            </p>
          </motion.div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-secondary/50 p-1">
              <TabsTrigger value="track" className="data-[state=active]:bg-white">
                <Search className="h-4 w-4 mr-2" />
                Track HBL
              </TabsTrigger>
              <TabsTrigger value="import" className="data-[state=active]:bg-white">
                <Upload className="h-4 w-4 mr-2" />
                Bulk Import
              </TabsTrigger>
              <TabsTrigger value="manual" className="data-[state=active]:bg-white">
                <FileText className="h-4 w-4 mr-2" />
                Manual HBLs
              </TabsTrigger>
            </TabsList>

            {/* Track HBL Tab */}
            <TabsContent value="track" className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-surface p-6"
              >
                <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <FileText className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Enter HBL number (e.g., KOP260121817)"
                      value={hblNumber}
                      onChange={(e) => setHblNumber(e.target.value.toUpperCase())}
                      className="input-europacific pl-12 text-lg h-14"
                      data-testid="hbl-search-input"
                    />
                  </div>
                  <div className="sm:w-52">
                    <select
                      value={coloader}
                      onChange={(e) => setColoader(e.target.value)}
                      className="w-full h-14 px-4 rounded-lg border border-border bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground"
                      data-testid="coloader-select"
                    >
                      <option value="">Select co-loader...</option>
                      {LCL_COLOADERS.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="btn-primary h-14 px-8"
                    disabled={loading}
                    data-testid="hbl-search-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="h-5 w-5 mr-2" />
                        Track
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-sm text-muted-foreground mt-4">
                  Select your co-loader/NVOCC if known. Tracks from Freightlinks, manual entries, or sends alert to operations for other co-loaders.
                </p>
              </motion.div>

              {/* Tracking Result */}
              <AnimatePresence mode="wait">
                {trackingResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-6"
                    data-testid="hbl-tracking-result"
                  >
                    {/* Main Info Card */}
                    <div className="card-surface p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
                        <div className="flex items-center gap-4">
                          <div className="h-14 w-14 rounded-lg bg-accent/10 flex items-center justify-center">
                            <FileText className="h-7 w-7 text-accent" strokeWidth={1.5} />
                          </div>
                          <div>
                            <p className="font-code text-2xl font-bold text-foreground">
                              {trackingResult.hbl_number}
                            </p>
                            <p className="text-muted-foreground">
                              Source: {trackingResult.source === "freightlinks" ? "Freightlinks" : "Manual Entry"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <ReminderButton
                            referenceType="hbl"
                            referenceNumber={trackingResult.hbl_number}
                            getAuthHeaders={getAuthHeaders}
                          />
                          <div className={`status-badge ${getStatusBadgeClass(trackingResult.status)}`}>
                            <span className="h-2 w-2 rounded-full bg-current mr-2" />
                            {trackingResult.status}
                          </div>
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {trackingResult.container_number && (
                          <div className="bg-secondary/50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 text-muted-foreground mb-2">
                              <Container className="h-4 w-4" />
                              <span className="text-sm">Container</span>
                            </div>
                            <p className="font-code font-semibold text-foreground">
                              {trackingResult.container_number}
                            </p>
                          </div>
                        )}

                        <div className="bg-secondary/50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 text-muted-foreground mb-2">
                            <MapPin className="h-4 w-4" />
                            <span className="text-sm">Route</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {trackingResult.origin || "N/A"} → {trackingResult.destination || "N/A"}
                          </p>
                        </div>

                        {trackingResult.current_location && (
                          <div className="bg-accent/10 p-4 rounded-lg border border-accent/20">
                            <div className="flex items-center gap-2 text-accent mb-2">
                              <MapPin className="h-4 w-4" />
                              <span className="text-sm font-medium">Current Location</span>
                            </div>
                            <p className="font-semibold text-foreground">
                              {trackingResult.current_location}
                            </p>
                          </div>
                        )}

                        {trackingResult.eta && (
                          <div className="bg-secondary/50 p-4 rounded-lg">
                            <div className="flex items-center gap-2 text-muted-foreground mb-2">
                              <Calendar className="h-4 w-4" />
                              <span className="text-sm">ETA</span>
                            </div>
                            <p className="font-semibold text-foreground">
                              {trackingResult.eta}
                            </p>
                          </div>
                        )}

                        <div className="bg-secondary/50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 text-muted-foreground mb-2">
                            <Box className="h-4 w-4" />
                            <span className="text-sm">Cargo</span>
                          </div>
                          <p className="font-semibold text-foreground">
                            {trackingResult.pieces || "N/A"} pcs / {trackingResult.weight || "N/A"} kg
                          </p>
                        </div>
                      </div>

                      {/* Notes */}
                      {trackingResult.notes && (
                        <div className="mt-4 p-4 bg-primary/5 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-1">Notes</p>
                          <p className="text-foreground">{trackingResult.notes}</p>
                        </div>
                      )}

                      {/* Advanced Options - Europacific Staff Only */}
                      {isEuropacificUser && (
                        <div className="mt-4 border-t border-border pt-4">
                          <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                            data-testid="show-advanced-options-btn"
                          >
                            <Settings className="h-4 w-4" />
                            {showAdvanced ? "Hide" : "Show"} Notification Settings
                            {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                          
                          <AnimatePresence>
                            {showAdvanced && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="mt-4 space-y-4 bg-secondary/30 p-4 rounded-lg"
                              >
                                <div>
                                  <Label className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                                    <Tag className="h-4 w-4" />
                                    Europacific Reference Number
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="e.g., EP-2026-001234"
                                    value={europacificRef}
                                    onChange={(e) => setEuropacificRef(e.target.value)}
                                    className="bg-white"
                                    data-testid="europacific-ref-input"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                                    <Mail className="h-4 w-4" />
                                    Customer Notification Emails
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="email1@company.com, email2@company.com"
                                    value={notificationEmails}
                                    onChange={(e) => setNotificationEmails(e.target.value)}
                                    className="bg-white"
                                    data-testid="notification-emails-input"
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Separate multiple emails with commas. These recipients will receive status updates.
                                  </p>
                                </div>
                                
                                <Button
                                  onClick={handleSaveNotifications}
                                  disabled={savingNotifications}
                                  className="btn-primary"
                                  data-testid="save-notifications-btn"
                                >
                                  {savingNotifications ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  ) : (
                                    <Save className="h-4 w-4 mr-2" />
                                  )}
                                  Save Notification Settings
                                </Button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>

                    {/* Events Timeline */}
                    {trackingResult.events && trackingResult.events.length > 0 && (
                      <div className="card-surface p-6">
                        <button
                          onClick={() => setShowEvents(!showEvents)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h3 className="font-heading text-lg font-bold text-foreground">
                            Events ({trackingResult.events.length})
                          </h3>
                          {showEvents ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </button>

                        <AnimatePresence>
                          {showEvents && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="mt-6 space-y-4"
                            >
                              {[...trackingResult.events].reverse().map((event, index) => (
                                <div
                                  key={index}
                                  className="flex gap-4 pb-4 border-b border-border last:border-0"
                                >
                                  <div
                                    className={`h-6 w-6 rounded-full flex items-center justify-center ${
                                      index === 0
                                        ? "bg-accent"
                                        : "bg-secondary border border-border"
                                    }`}
                                  >
                                    <div
                                      className={`h-2 w-2 rounded-full ${
                                        index === 0 ? "bg-white" : "bg-muted-foreground"
                                      }`}
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-foreground">{event.status}</p>
                                    {event.note && (
                                      <p className="text-sm text-muted-foreground">{event.note}</p>
                                    )}
                                    <p className="text-xs text-muted-foreground font-code mt-1">
                                      {new Date(event.date).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {/* Photos Section */}
                    {photos.length > 0 && (
                      <div className="card-surface p-6">
                        <button
                          onClick={() => setShowPhotos(!showPhotos)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
                            <Camera className="h-5 w-5 text-accent" />
                            Photos ({photos.length})
                          </h3>
                          {showPhotos ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </button>

                        <AnimatePresence>
                          {showPhotos && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="mt-6"
                            >
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {photos.map((photo) => (
                                  <div
                                    key={photo.id}
                                    className="relative group cursor-pointer rounded-lg overflow-hidden border border-border hover:border-accent transition-colors"
                                    onClick={() => setSelectedPhoto(photo)}
                                  >
                                    <img
                                      src={`${API_URL}/photos/file/${photo.id}`}
                                      alt={photo.filename}
                                      className="w-full h-32 object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <Image className="h-6 w-6 text-white" />
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2">
                                      <p className="text-xs text-white truncate">{photo.filename}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {/* Photo Lightbox */}
                    {selectedPhoto && (
                      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
                        <DialogContent className="bg-black border-none max-w-4xl">
                          <img
                            src={`${API_URL}/photos/file/${selectedPhoto.id}`}
                            alt={selectedPhoto.filename}
                            className="w-full max-h-[80vh] object-contain"
                          />
                          <div className="text-center mt-4">
                            <p className="text-white text-sm">{selectedPhoto.filename}</p>
                            <p className="text-gray-400 text-xs">{new Date(selectedPhoto.uploaded_at).toLocaleString()}</p>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>

            {/* Bulk Import Tab */}
            <TabsContent value="import" className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-surface p-6"
              >
                <div className="mb-6">
                  <h2 className="font-heading text-xl font-bold text-foreground mb-2">
                    Bulk Import from Email
                  </h2>
                  <p className="text-muted-foreground">
                    Paste your weekly shipment email below. The system will automatically extract vessel, container, and HBL information.
                  </p>
                </div>

                {/* Drag & Drop Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="flex flex-col items-center justify-center text-center mb-4">
                    <Upload className={`h-10 w-10 mb-3 ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
                    <p className="text-foreground font-medium">
                      Drag & drop email text or paste below
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Supports .txt files or direct text paste
                    </p>
                  </div>

                  <Textarea
                    value={emailContent}
                    onChange={(e) => setEmailContent(e.target.value)}
                    placeholder={`Paste email content here...

Example format:
VSL/VOY : CMA CGM J. MADISON 0BEMTW1MA
ETD/ETA : 1.14-2.22
CNTR NO :
SEKU9018093 / 28471882 / 42RH
MBL NO : COSU6434411880
HBL NO :

AKOP26010002 - DAP, FPOD IS KOMAROM, HUNGARY
AKOP26010003 - OBL, DAP, FPOD IS RACALMAS, HUNGARY
...`}
                    className="min-h-[300px] font-code text-sm bg-white border-border"
                    data-testid="bulk-import-textarea"
                  />
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    onClick={handleBulkImport}
                    className="btn-primary"
                    disabled={importLoading || !emailContent.trim()}
                    data-testid="bulk-import-btn"
                  >
                    {importLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <Upload className="h-5 w-5 mr-2" />
                    )}
                    Import HBLs
                  </Button>
                </div>

                {/* Import Result */}
                {importResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-6 p-6 rounded-lg ${
                      importResult.success ? "bg-success/10 border border-success/20" : "bg-destructive/10 border border-destructive/20"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {importResult.success ? (
                        <CheckCircle className="h-6 w-6 text-success flex-shrink-0" />
                      ) : (
                        <AlertCircle className="h-6 w-6 text-destructive flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-foreground mb-3">
                          {importResult.success ? "Import Successful" : "Import Failed"}
                        </h3>

                        {importResult.success && (
                          <>
                            {/* Shipment Info */}
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                              {importResult.vessel && (
                                <div className="bg-white/50 p-3 rounded">
                                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                    <Ship className="h-4 w-4" />
                                    Vessel
                                  </div>
                                  <p className="font-medium text-foreground">{importResult.vessel}</p>
                                  {importResult.voyage && (
                                    <p className="text-sm text-muted-foreground">Voy: {importResult.voyage}</p>
                                  )}
                                </div>
                              )}
                              {importResult.container_number && (
                                <div className="bg-white/50 p-3 rounded">
                                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                    <Container className="h-4 w-4" />
                                    Container
                                  </div>
                                  <p className="font-code font-medium text-foreground">{importResult.container_number}</p>
                                </div>
                              )}
                              {importResult.eta && (
                                <div className="bg-white/50 p-3 rounded">
                                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                    <Calendar className="h-4 w-4" />
                                    ETA
                                  </div>
                                  <p className="font-medium text-foreground">{importResult.eta}</p>
                                </div>
                              )}
                              {importResult.mbl_number && (
                                <div className="bg-white/50 p-3 rounded">
                                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                    <FileText className="h-4 w-4" />
                                    MBL
                                  </div>
                                  <p className="font-code font-medium text-foreground">{importResult.mbl_number}</p>
                                </div>
                              )}
                            </div>

                            {/* Stats */}
                            <div className="flex gap-4 mb-4">
                              <span className="px-3 py-1 bg-success/20 text-success rounded-full text-sm font-medium">
                                {importResult.hbls_created} Created
                              </span>
                              <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium">
                                {importResult.hbls_updated} Updated
                              </span>
                            </div>

                            {/* HBL List */}
                            {importResult.hbls && importResult.hbls.length > 0 && (
                              <div className="bg-white/50 rounded p-4 max-h-60 overflow-y-auto">
                                <p className="text-sm font-medium text-foreground mb-2">Imported HBLs:</p>
                                <div className="space-y-1">
                                  {importResult.hbls.map((hbl, index) => (
                                    <div key={index} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                                      <span className="font-code text-sm">{hbl.hbl_number}</span>
                                      <div className="flex items-center gap-2">
                                        {hbl.destination && (
                                          <span className="text-xs text-muted-foreground">{hbl.destination}</span>
                                        )}
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                          hbl.action === "created" ? "bg-success/20 text-success" : "bg-primary/20 text-primary"
                                        }`}>
                                          {hbl.action}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {importResult.errors && importResult.errors.length > 0 && (
                          <div className="mt-3">
                            {importResult.errors.map((error, index) => (
                              <p key={index} className="text-sm text-destructive">{error}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </TabsContent>

            {/* Manual HBLs Tab */}
            <TabsContent value="manual" className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-surface p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">
                      Manual HBL Entries
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {manualHbls.length} HBLs stored
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={fetchManualHbls}
                      className="border-border"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                      <DialogTrigger asChild>
                        <Button
                          className="btn-primary"
                          onClick={() => {
                            setEditingHbl(null);
                            resetForm();
                          }}
                          data-testid="add-hbl-btn"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add HBL
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="font-heading text-foreground">
                            {editingHbl ? `Edit HBL: ${editingHbl}` : "Add New HBL"}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-foreground">HBL Number *</Label>
                              <Input
                                value={formData.hbl_number}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    hbl_number: e.target.value.toUpperCase(),
                                  }))
                                }
                                placeholder="e.g., AKOP26010002"
                                className="input-europacific"
                                disabled={!!editingHbl}
                                data-testid="hbl-form-number"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-foreground">Container Number</Label>
                              <Input
                                value={formData.container_number}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    container_number: e.target.value.toUpperCase(),
                                  }))
                                }
                                placeholder="e.g., SEKU9018093"
                                className="input-europacific"
                                data-testid="hbl-form-container"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-foreground">Origin</Label>
                              <Input
                                value={formData.origin}
                                onChange={(e) =>
                                  setFormData((prev) => ({ ...prev, origin: e.target.value }))
                                }
                                placeholder="e.g., Shanghai, China"
                                className="input-europacific"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-foreground">Destination</Label>
                              <Input
                                value={formData.destination}
                                onChange={(e) =>
                                  setFormData((prev) => ({ ...prev, destination: e.target.value }))
                                }
                                placeholder="e.g., Koper, Slovenia"
                                className="input-europacific"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-foreground">Status</Label>
                              <select
                                value={formData.status}
                                onChange={(e) =>
                                  setFormData((prev) => ({ ...prev, status: e.target.value }))
                                }
                                className="w-full h-12 px-3 rounded-md border border-border bg-white focus:border-primary focus:ring-2 focus:ring-primary/20"
                              >
                                <option value="Pending">Pending</option>
                                <option value="Processing">Processing</option>
                                <option value="Shipped">Shipped</option>
                                <option value="In Transit">In Transit</option>
                                <option value="Arrived at Port">Arrived at Port</option>
                                <option value="Customs Clearance">Customs Clearance</option>
                                <option value="Ready for Delivery">Ready for Delivery</option>
                                <option value="Delivered">Delivered</option>
                              </select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-foreground">ETA</Label>
                              <Input
                                type="date"
                                value={formData.eta}
                                onChange={(e) =>
                                  setFormData((prev) => ({ ...prev, eta: e.target.value }))
                                }
                                className="input-europacific"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-foreground">Notes</Label>
                            <Textarea
                              value={formData.notes}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, notes: e.target.value }))
                              }
                              placeholder="e.g., DAP, FPOD IS KOMAROM, HUNGARY"
                              className="bg-white border-border"
                            />
                          </div>

                          <Button
                            onClick={handleAddHbl}
                            className="btn-primary w-full"
                            disabled={loading}
                            data-testid="hbl-form-submit"
                          >
                            {loading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : editingHbl ? (
                              "Update HBL"
                            ) : (
                              "Create HBL"
                            )}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* HBLs Table */}
                {manualHbls.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No manual HBL entries yet</p>
                    <p className="text-sm text-muted-foreground mb-4">Use Bulk Import to quickly add HBLs from your weekly email</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className="text-muted-foreground">HBL Number</TableHead>
                          <TableHead className="text-muted-foreground">Container</TableHead>
                          <TableHead className="text-muted-foreground">Destination</TableHead>
                          <TableHead className="text-muted-foreground">ETA</TableHead>
                          <TableHead className="text-muted-foreground">Status</TableHead>
                          <TableHead className="text-muted-foreground">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {manualHbls.map((hbl) => (
                          <TableRow key={hbl.id} className="border-border">
                            <TableCell className="font-code font-medium text-foreground">
                              {hbl.hbl_number}
                            </TableCell>
                            <TableCell className="font-code text-muted-foreground">
                              {hbl.container_number || "-"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {hbl.destination || "-"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {hbl.eta || "-"}
                            </TableCell>
                            <TableCell>
                              <span className={`status-badge ${getStatusBadgeClass(hbl.status)}`}>
                                {hbl.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEdit(hbl)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteHbl(hbl.hbl_number)}
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

export { LCLTrackingPage };
